import { useMutation, useReactiveVar } from '@apollo/client'
import HoverTooltip from '@deltek/specification-client/core/Feedbacks/HoverTooltip'
import { ContentItem } from '@deltek/specification-client/core/Models/contentItem'
import { TextContentElement } from '@deltek/specification-client/core/Models/textContentElement'
import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import usePrevious from '@deltek/specification-client/core/Utilities/Hooks/usePrevious'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { Typography } from '@material-ui/core'
import { Theme, makeStyles } from '@material-ui/core/styles'
import { SetProjectContentItemElements } from 'api/graphQL/mutations/projectProductFamily.mutations'
import {
    resetOnFocusProperty,
    setPropertyToFocus,
    workspaceProperty,
} from 'api/graphQL/reactiveVars/property'
import { userInfo } from 'api/graphQL/reactiveVars/user'
import { setReactiveWorkspaceActions } from 'api/graphQL/reactiveVars/workspaceActions'
import {
    isOmWorkspaceUser,
    setWorkspaceContentItems,
    workspaceContents,
} from 'api/graphQL/reactiveVars/workspaceContent'
import { showHideToggles } from 'api/graphQL/reactiveVars/workspaceShowHideToggles'
import clsx from 'clsx'
import { TrackChangeType } from 'models/changes'
import { SetProjectContentItemElementsInput } from 'models/setProjectContentItemElementsInput'
import {
    defaultColor,
    defaultOOFColor,
    manufacturerSecondaryColor,
    manufacturerSecondaryColorOOFColor,
} from 'pages/ProjectsPage/colorVars'
import React, { Suspense } from 'react'
import { useParams } from 'react-router-dom'
import useGlobalSnackbar from 'shared/utils/useGlobalSnackbar'
import { LoadingSkeleton } from '../LoadingSkeleton'
import { getUpdatedItemsArray } from '../WorkspaceContentArea'
import { WorkspaceContentContext } from '../WorkspaceContentContext'
import { AreaContext } from './AreaContext'
import { DragDropAreaContext } from './DragDropAreaContext'
import { DraggableItemStyleProps } from './DraggableItem'
import { EditArticleErrorMessage, articleHasDuplicateKey } from './addUtils'
import { useCustomTransition } from './hooks/useCustomTransition'
import { NormalizedDocumentTemplateViewItem } from './model'
import {
    ProductTypeInFamilyViewContentType,
    WorkspaceTabTitles,
    isProductGroupIconShown,
} from './utils'

// Lazy load large dependencies
const GroupWorkOutlinedIcon = React.lazy(
    () => import('@material-ui/icons/GroupWorkOutlined')
)
const GroupWorkIcon = React.lazy(() => import('@material-ui/icons/GroupWork'))
const PropertyEditorEditableText = React.lazy(
    () => import('../PropertyEditor/PropertyEditorEditableText')
)

const blackListElementText = [
    'insert drawing designation',
    'insert product designation',
]

const forbiddenArticleCharacters = [':', '|']

export const getTitle = (
    property: Partial<NormalizedDocumentTemplateViewItem>,
    partType: string,
    contentItemIndex: number,
    isSMM?: boolean,
    isProductTypeInFamilyView?: boolean,
    isAEUserProjectWorkspace?: boolean
) => {
    const suffix = (contentItemIndex + 1).toString()
    let title = ''
    if (partType?.toLocaleLowerCase() === 'products') {
        if (!isProductTypeInFamilyView) {
            title =
                property.isHidden ||
                (property?.isDeactivated && isAEUserProjectWorkspace)
                    ? '_._\xa0\xa0\xa0'
                    : `2.${suffix}\xa0\xa0\xa0`
        } else {
            // For Family Product type group: Create uppercase alphabet prefix
            // Based on ContentItem.tsx
            let prefix = ''
            let isToRepeat = false
            let times = 0
            const uppercaseAlpha = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('')
            let actualIndex = contentItemIndex
            if (actualIndex > uppercaseAlpha.length - 1) {
                actualIndex = actualIndex % uppercaseAlpha.length
                isToRepeat = true
                times = Math.floor(contentItemIndex / uppercaseAlpha.length)
            }
            prefix = `${
                property?.isHidden && property?.isDeactivated
                    ? '_'
                    : uppercaseAlpha[
                          property?.paragraphOrder &&
                          !property.isHidden &&
                          !property?.isDeactivated &&
                          property?.paragraphOrder - actualIndex < 1
                              ? property?.paragraphOrder - 1
                              : actualIndex
                      ]
            }.`
            switch (times) {
                case 2:
                    prefix = prefix[0] + prefix
                case 1:
                    prefix = prefix[0] + prefix
                    break
                default:
                    break
            }
            title = `${prefix}\xa0\xa0\xa0`
        }
    } else if (partType?.toLocaleLowerCase() === 'execution') {
        title =
            property.isHidden ||
            (property?.isDeactivated && isAEUserProjectWorkspace)
                ? '_._\xa0\xa0\xa0'
                : `3.${suffix}\xa0\xa0\xa0`
    } else {
        title =
            property.isHidden ||
            (property?.isDeactivated && isAEUserProjectWorkspace)
                ? '_._\xa0\xa0\xa0'
                : `1.${suffix}\xa0\xa0\xa0`
    }

    title = property.contentItem?.elements?.reduce((prev, currentValue) => {
        if (
            currentValue?.elements?.length! > 0 &&
            (currentValue?.type === 'Text' ||
                currentValue?.type === 'NewlyAdded')
        ) {
            const elementsText = currentValue?.elements?.map(
                (element) => element.text
            )
            const sanitizedElement = sanitizeElementText(elementsText)

            return `${prev} ${sanitizedElement?.toString().trim()}`
        } else if (
            currentValue?.text !== '' &&
            (currentValue?.type === 'Text' ||
                currentValue?.type === 'NewlyAdded')
        ) {
            return `${prev}${currentValue?.text}`
        } else {
            return prev
        }
    }, title) as string

    if (!!isProductTypeInFamilyView && (title.indexOf(':') || -1) >= 0) {
        // For Family Product type group: Display key only
        title = title.split(':')[0]
    }

    return title
}

export const sanitizeElementText = (
    elementText: (string | undefined)[] | undefined
) => {
    let sanitizedText = elementText
    if (elementText) {
        sanitizedText = elementText.filter(
            (text) =>
                !blackListElementText.includes((text as string)?.toLowerCase())
        )
    }

    return sanitizedText
}

export interface EditableArticleProps {
    isEditable: boolean
    propertyId: string
    articleTitle: string
    articleId: string | undefined
    isNewlyAddedArticle: boolean
    isHidden: boolean
    handleSaveNewArticle: (event: any) => void
    onCancelAddProperty: () => void
    getPropertyEditingDetails: (
        property: NormalizedDocumentTemplateViewItem,
        isForArticle: boolean,
        nodeId: string,
        contentItemParentId: string,
        parentType: string,
        parentKey?: string
    ) => SetProjectContentItemElementsInput
    elements?: Partial<TextContentElement>[]
    contentTeamFromStudioPage?: boolean
    disableArticleExpandInFullView?: boolean
    isPremiumPlus: boolean
    template?: NormalizedDocumentTemplateViewItem
    parent?: NormalizedDocumentTemplateViewItem
    productTypeInFamilyViewContentType?: ProductTypeInFamilyViewContentType
}

const useStyles = makeStyles((theme: Theme) => ({
    titleTextFlex: {
        flex: 1,
    },
    ptGroupText: { flex: 1 },
    titleText: {
        fontWeight: 400,
        fontStyle: (props: Partial<DraggableItemStyleProps>) =>
            props.isNewlyAddedArticle ? 'italic' : 'normal',
        color: (props: Partial<DraggableItemStyleProps>) => {
            return props.disableArticleExpandInFullView
                ? defaultOOFColor
                : props?.isHidden
                ? props?.isPremiumPlus
                    ? manufacturerSecondaryColorOOFColor
                    : defaultOOFColor
                : (!!props?.changeType &&
                      props.changeType.toUpperCase() !==
                          TrackChangeType.ACCEPTED &&
                      props.isChangeToggle) ||
                  props.isNewlyAddedArticle
                ? '#FF6060'
                : props?.isPremiumPlus
                ? manufacturerSecondaryColor
                : defaultColor
        },
        fontSize: pxToRem(18),
        textDecoration: 'unset',
    },
    titleAdornment: {
        marginRight: theme.spacing(0.9),
        paddingTop: theme.spacing(0.2),
    },
    inputRoot: {
        width: '100%',
        alignItems: 'center',
    },
    inputRootFlex: {
        display: 'flex',
    },
    hasError: {
        borderBottom: '1px solid red',
    },
    highlightedArticle: {
        border: '1px solid rgba(0, 152, 214, 0.5)',
        boxShadow: '0px 1px 4px rgba(0, 152, 214, 0.65)',
        borderRadius: pxToRem(100),
        padding: theme.spacing(0, 1),
        display: 'inline-flex',
        width: 'auto',
    },
    newArticle: {
        width: '100%',
        display: 'flex',
    },
}))

const Article: React.FC<EditableArticleProps> = ({
    isEditable,
    propertyId,
    articleTitle,
    isNewlyAddedArticle,
    isHidden,
    handleSaveNewArticle,
    onCancelAddProperty,
    elements,
    articleId,
    contentTeamFromStudioPage,
    isPremiumPlus,
    disableArticleExpandInFullView,
    getPropertyEditingDetails,
    template,
    parent,
    productTypeInFamilyViewContentType,
}) => {
    const {
        handleEditKeyChanges,
        retrieveUpdatedElementChanges,
        selectedProjectNode,
    } = React.useContext(AreaContext)
    const { typeForFullView } = React.useContext(DragDropAreaContext)
    const [hasChanges, setHasChanges] = React.useState(false)
    const [articleName, setArticleName] = React.useState(
        articleTitle?.split('\xa0\xa0\xa0')?.[1]
    )
    const hasPreviousChanges = usePrevious(hasChanges)
    const [articleWithIconPrefix, setArticleWithIconPrefix] = React.useState('')
    const elementRef = React.useRef<any | undefined | null>()
    const [editorRef, setEditorRef] = React.useState<
        React.RefObject<HTMLElement> | HTMLElement
    >()

    const { projectId } = useParams<{ projectId?: string }>()

    const { propertyToFocus, optionGroupToFocus } = useReactiveVar(
        workspaceProperty
    )
    const { handleRetrieveUpdatedDTV, contentItemWorker } = React.useContext(
        WorkspaceContentContext
    )

    const { isTrackChangeToggleOn: isTrackChangeToggleOn } = useReactiveVar(
        showHideToggles
    )
    const {
        isAdmin,
        currentUserInfo: { kpUserId },
    } = useReactiveVar(userInfo)

    const isOMUser = useReactiveVar(isOmWorkspaceUser)

    const classes = useStyles({
        isNewlyAddedArticle,
        isHidden: isHidden || template?.isDeactivated,
        changeType: elements![0]?.changeType as TrackChangeType,
        isChangeToggle: isTrackChangeToggleOn,
        isPremiumPlus,
        disableArticleExpandInFullView,
    })

    const { showGlobalSnackbar } = useGlobalSnackbar()
    const [_, startTransition] = useCustomTransition()

    const [updateProjectContentItemElement] = useMutation(
        SetProjectContentItemElements(6),
        {
            onCompleted: async (data: any) => {
                startTransition(() => {
                    handleRetrieveUpdatedDTV()
                    retrieveUpdatedElementChanges()
                })
                startTransition(() => {
                    const {
                        contentItemNodes,
                        dtv,
                        tabSelected,
                    } = workspaceContents()

                    const updatedContentItems = getUpdatedItemsArray(
                        contentItemNodes ?? [],
                        data?.setProjectContentItemElements?.contentItemNodes ??
                            ([] as Partial<ContentItem>[]),
                        true,
                        false
                    )
                    contentItemWorker.postMessage({
                        dtv: dtv?.items ?? [],
                        selectedTab: WorkspaceTabTitles[tabSelected] as string,
                        workspaceContentItems: contentItemNodes ?? [],
                    })
                    contentItemWorker.onmessage = (
                        event: MessageEvent<ContentItem[]>
                    ) => {
                        setHasChanges(false)
                        setWorkspaceContentItems(
                            updatedContentItems.concat(event.data)
                        )
                    }
                })
                showGlobalSnackbar({
                    variables: {
                        message: 'Changes Saved Successfully',
                        variant: 'success',
                        id: `EditableArticle-UpdateProjectContentItemSuccess`,
                    },
                })
            },
            onError: (error) => {
                setHasChanges(false)
                showGlobalSnackbar({
                    variables: {
                        message: `There was a problem saving your changes.`,
                        variant: 'error',
                        id: 'EditableArticle-UpdateProjectContentItemError',
                    },
                })
            },
        }
    )

    const handleMouseLeave = React.useCallback(() => {
        resetOnFocusProperty()
    }, [resetOnFocusProperty])

    const handleChange = React.useCallback(
        (event) => {
            if (!hasChanges) {
                setHasChanges(true)
            }
        },
        [hasChanges]
    )

    const handleOnFocus = React.useCallback(
        (element) => () => {
            if (element.type !== 'NewlyAdded' && !!element?.changeType) {
                setPropertyToFocus(element.id, template?.id)
            }

            if (propertyToFocus && !element?.changeType) {
                resetOnFocusProperty(false)
            }

            setReactiveWorkspaceActions({
                isInsertPill: false,
                isInsertText: false,
                isInsertUOM: false,
                isAddSpecifierNote:
                    !isHidden &&
                    contentTeamFromStudioPage &&
                    (isOMUser || !isAdmin),
            })
        },
        [
            elements,
            propertyToFocus,
            setPropertyToFocus,
            isOMUser,
            isAdmin,
            isHidden,
            setReactiveWorkspaceActions,
        ]
    )

    const handleArticleChanges = React.useCallback(
        (newTextContentElement: TextContentElement[], textValue: string) => {
            if (
                !contentTeamFromStudioPage &&
                !template?.addingState &&
                !template?.isUserAdded
            )
                return
            if (isNewlyAddedArticle) {
                // Sending text to parent function
                // it expects the value like this object
                startTransition(() => {
                    handleSaveNewArticle({
                        currentTarget: { value: textValue },
                    })
                })
                handleMouseLeave()
            } else {
                const editingInput = getPropertyEditingDetails?.(
                    template!,
                    true,
                    selectedProjectNode?.id ?? '',
                    template?.parentId ?? '',
                    template?.parentType ?? '',
                    template?.parentKey ?? ''
                ) as SetProjectContentItemElementsInput
                const elementsArrayCopy = [...elements!]
                const index = elementsArrayCopy?.findIndex(
                    (elem: any) => elem.id === newTextContentElement[0]?.id
                )
                elementsArrayCopy?.splice(index!, 1, ...newTextContentElement)

                // reducing the edited elements to get the updated element and updated text
                const {
                    updatedElements,
                    updatedText,
                }: any = elementsArrayCopy?.reduce(
                    (
                        acc: {
                            updatedElements: Partial<TextContentElement>[]
                            updatedText: string
                        },
                        curr: TextContentElement | any
                    ) => {
                        const {
                            isOptionItem,
                            fieldId,
                            isPremiumPlus,
                            isBestPractice,
                            isGlobalBestPractice,
                            __typename,
                            ...apiReadyInput
                        } = curr
                        acc.updatedElements.push(apiReadyInput)
                        if (curr?.changeType?.toUpperCase() !== 'DELETED') {
                            acc.updatedText = acc.updatedText + curr.text
                        }
                        return acc
                    },
                    { updatedElements: [], updatedText: '' }
                )

                const isKeyError = articleHasDuplicateKey(
                    updatedText,
                    template?.partType,
                    updatedElements[0]?.id
                )

                if (isKeyError || !updatedText?.trim()) {
                    ;(editorRef as HTMLElement)?.focus()
                    showGlobalSnackbar({
                        variables: {
                            message: EditArticleErrorMessage,
                            variant: 'error',
                            id: 'EditableArticle- edit error',
                        },
                    })
                    return
                } else {
                    const {
                        tabSelected,
                        projectProductFamilyId,
                    } = workspaceContents()
                    updateProjectContentItemElement({
                        variables: {
                            input: {
                                ...editingInput,
                                propertyKey: articleName,
                                elements: updatedElements,
                            },
                            partType:
                                contentTeamFromStudioPage && typeForFullView
                                    ? typeForFullView
                                    : WorkspaceTabTitles[tabSelected ?? 0],
                            projectId,
                            rootArticleContentItemIds: !!articleId
                                ? [articleId]
                                : [],
                            productFamilyId: projectProductFamilyId,
                            isTrackChangeToggleOn: isTrackChangeToggleOn,
                        },
                    })
                }
            }
        },
        [
            isNewlyAddedArticle,
            editorRef,
            projectId,
            articleId,
            handleSaveNewArticle,
            getPropertyEditingDetails,
            handleEditKeyChanges,
            showGlobalSnackbar,
            updateProjectContentItemElement,
            handleMouseLeave,
            articleName,
            isTrackChangeToggleOn,
            typeForFullView,
            contentTeamFromStudioPage,
            template,
            elements,
        ]
    )

    React.useEffect(() => {
        if (
            propertyToFocus &&
            propertyToFocus === elements?.[0]?.id &&
            elementRef
        ) {
            setTimeout(function () {
                elementRef?.current?.scrollIntoView?.({
                    block: 'center',
                })
            }, 1000)
        }
    }, [propertyToFocus, elements, elementRef])

    React.useEffect(() => {
        if (
            articleTitle?.split('\xa0\xa0\xa0')?.[1] !== articleName &&
            !hasChanges
        ) {
            setArticleName(articleTitle?.split('\xa0\xa0\xa0')?.[1])
        }
    }, [articleTitle, hasChanges, articleName])

    const renderElements = React.useCallback(() => {
        return elements?.map((element, index) => {
            const editableTextVariant =
                element.type === 'NewlyAdded' ? 'newlyadded' : 'plain'
            return !isTrackChangeToggleOn &&
                element?.changeType?.toUpperCase() === 'DELETED' ? null : (
                <span
                    key={`article_${element.id}`}
                    data-testid={`spanElement${element.id}`}
                    className={clsx(
                        ((propertyToFocus && propertyToFocus === element?.id) ||
                            optionGroupToFocus?.some(
                                (item) => item === element?.id || ''
                            )) &&
                            classes.highlightedArticle,
                        {
                            [classes.newArticle]: element.type === 'NewlyAdded',
                        }
                    )}
                    onFocus={handleOnFocus(element)}
                    id={`ciet-${template?.id}`}
                >
                    <Suspense fallback={<LoadingSkeleton />}>
                        <PropertyEditorEditableText
                            element={{ ...element, type: 'Article' }}
                            key={`${element?.id}`}
                            identifier={`articleSpanElementText-${element?.id}`}
                            editable={true}
                            onChange={handleChange}
                            editorRef={setEditorRef}
                            data-testid={`articleSpanElementText-${element?.id}`}
                            value={`${
                                !!productTypeInFamilyViewContentType &&
                                index === 0
                                    ? '\xa0\xa0\xa0'
                                    : ''
                            }${element?.text}`}
                            isToUpdateElement={
                                hasPreviousChanges && !hasChanges
                            }
                            productTypeInFamilyViewContentType={
                                !!productTypeInFamilyViewContentType &&
                                productTypeInFamilyViewContentType ===
                                    ProductTypeInFamilyViewContentType.ARTICLEPRODUCTTYPE
                            }
                            isDeleteFormatted={
                                (!element?.authoredUserId ||
                                    kpUserId !== element?.authoredUserId!) ??
                                false
                            }
                            additionalBlockedCharacters={
                                forbiddenArticleCharacters
                            }
                            saveChanges={handleArticleChanges}
                            variant={editableTextVariant}
                            changeType={element?.changeType as TrackChangeType}
                            template={template}
                            contentTeamFromStudioPage={
                                contentTeamFromStudioPage
                            }
                            isUserAdded={false}
                            disableArticleExpandInFullView={
                                disableArticleExpandInFullView
                            }
                            isNewlyAddedArticle={isNewlyAddedArticle}
                            isHidden={isHidden}
                            onCancelAddProperty={onCancelAddProperty}
                            parentTemplate={parent}
                        />
                    </Suspense>
                </span>
            )
        })
    }, [
        elements,
        template,
        kpUserId,
        handleArticleChanges,
        isTrackChangeToggleOn,
        setEditorRef,
        contentTeamFromStudioPage,
        isNewlyAddedArticle,
        isHidden,
        disableArticleExpandInFullView,
        hasPreviousChanges,
        hasChanges,
        optionGroupToFocus,
        propertyToFocus,
        parent,
    ])
    React.useEffect(() => {
        if (!!productTypeInFamilyViewContentType) {
            switch (productTypeInFamilyViewContentType) {
                case ProductTypeInFamilyViewContentType.PRODUCTGROUPICON:
                case ProductTypeInFamilyViewContentType.PARENTARTICLEPRODUCTGROUP:
                case ProductTypeInFamilyViewContentType.CHILDARTICLEPRODUCTTYPE:
                case ProductTypeInFamilyViewContentType.ARTICLEPRODUCTTYPE:
                    setArticleWithIconPrefix(
                        articleTitle?.split('\xa0\xa0\xa0')?.[0] +
                            '\xa0\xa0\xa0'
                    )
                    break
                default:
                    break
            }
        }
    }, [articleTitle, productTypeInFamilyViewContentType])

    return (
        <div
            ref={elementRef}
            className={clsx(
                classes.inputRoot,
                articleTitle?.length < 99 || isNewlyAddedArticle
                    ? classes.inputRootFlex
                    : ''
            )}
            key={`article-${elements?.[0]?.id}`}
            data-testid={`parentArticleDiv${elements?.[0]?.id}`}
        >
            {isEditable &&
            !disableArticleExpandInFullView &&
            !productTypeInFamilyViewContentType ? (
                <>
                    <Typography
                        component="span"
                        variant="h6"
                        className={clsx(
                            classes.titleText,
                            classes.titleAdornment,
                            `{'aria-label' : {article-${elements?.[0]?.id}}}`,
                            isNewlyAddedArticle && parent?.isProduct
                                ? classes.titleTextFlex
                                : ''
                        )}
                        data-testid={`spanTitle${propertyId}`}
                    >
                        {articleTitle?.split('\xa0\xa0\xa0')?.[0]}
                    </Typography>
                    {renderElements()}
                </>
            ) : !!productTypeInFamilyViewContentType ? (
                <GridContainer
                    className={classes.titleText}
                    direction="row"
                    alignItems="center"
                >
                    <GridItem>
                        <Typography
                            component="span"
                            variant="h6"
                            className={clsx(
                                classes.titleText,
                                `{'aria-label' : {article-${elements?.[0]?.id}}}`
                            )}
                            data-testid={`spanTitlePrefix${propertyId}`}
                        >
                            {articleWithIconPrefix}
                        </Typography>
                    </GridItem>
                    <GridItem>
                        {isProductGroupIconShown(
                            productTypeInFamilyViewContentType
                        ) ? (
                            <HoverTooltip
                                text={`Product Group: ${articleName}`}
                                placements={['bottom', 'right']}
                            >
                                <GroupWorkOutlinedIcon
                                    data-testid={`groupWorkOutlinedIcon${propertyId}`}
                                    fontSize="small"
                                />
                            </HoverTooltip>
                        ) : (
                            <HoverTooltip
                                text={`Product Type: ${articleName}`}
                                placements={['bottom', 'right']}
                            >
                                <GroupWorkIcon
                                    data-testid={`groupWorkIcon${propertyId}`}
                                    fontSize="small"
                                />
                            </HoverTooltip>
                        )}
                    </GridItem>
                    <GridItem className={classes.ptGroupText}>
                        {renderElements()}
                    </GridItem>
                </GridContainer>
            ) : (
                <Typography
                    component="span"
                    variant="h6"
                    className={clsx(
                        classes.titleText,
                        `{'aria-label' : {article-${elements?.[0]?.id}}}`
                    )}
                    data-testid={`spanTitle${propertyId}`}
                >
                    {articleTitle}
                </Typography>
            )}
        </div>
    )
}

export default React.memo(Article)
